(() => {
  'use strict';

  // Mirrors the active Shopify automatic discount collections:
  // Vitamins & Supplements + Bundles, resolved 2026-09-02.
  const ELIGIBLE_VARIANT_IDS = new Set([
    47643999895851, 47644003369259, 47644016836907, 47644063596843,
    47644068086059, 47677683597611, 47963614904619, 48143743615275,
    48143754002731, 48143759343915, 48143794733355, 48143795847467,
    48143798862123, 48143802663211, 48143803842859, 48467327025451,
    49322549051691, 49322549838123, 49796836065579, 50041105416491,
    50041108103467, 50267982135595, 50286787952939, 50543062155563,
    50611944948011, 50842215350571, 50865750835499, 51341690241323,
    51345271914795, 51354244448555, 51354251690283, 51386970210603,
    51547651637547, 51547668480299, 51547680801067, 51547697742123,
    51547822031147, 51547827601707, 52029729145131, 52253783982379,
    52466649596203, 52466921275691, 52466921373995, 54127965864235,
    54127965897003, 54674683822379, 54674683855147
  ]);

  // The automatic discount ended 2026-09-30; ad traffic now gets the same deal
  // through code PRSPRB1G50 (applied by snippets/prspr-bogo-ad-activator).
  const BOGO_TITLES = new Set([
    'SEMI ANNUAL BOGO 50% SALE',
    'PRSPRB1G50',
    'SEPT BOGO 50% - AD LANDING PAGES ONLY (PRSPRB1G50)'
  ]);
  const DISCLAIMER = 'Lower-priced item in each eligible pair gets 50% off. At least one item in each pair must be a full-price purchase.';
  const ROOT_ID = 'prspr-sept-bogo-progress';
  let requestInFlight = false;
  let scheduled = 0;
  let lastSignature = '';

  function isEligible(item) {
    return ELIGIBLE_VARIANT_IDS.has(Number(item.variant_id || item.id));
  }

  function isSubscription(item) {
    return Boolean(item.selling_plan_allocation || item.selling_plan || item.sellingPlanAllocation);
  }

  function allocationTitle(allocation) {
    return String(
      allocation?.discount_application?.title ||
      allocation?.discountApplication?.title ||
      allocation?.title ||
      ''
    ).trim();
  }

  function allocationAmountCents(allocation) {
    const amount = allocation?.amount;
    if (typeof amount === 'number') return amount;
    if (typeof amount === 'string') return Math.round(Number(amount) * 100);
    if (amount && typeof amount.amount !== 'undefined') return Math.round(Number(amount.amount) * 100);
    return 0;
  }

  function bogoAllocations(item) {
    const allocations = [
      ...(item.line_level_discount_allocations || []),
      ...(item.discount_allocations || []),
      ...(item.discountAllocations || [])
    ];
    return allocations.filter((allocation) => BOGO_TITLES.has(allocationTitle(allocation).toUpperCase()));
  }

  function eligibleQuantities(cart) {
    return (cart.items || cart.lines || []).reduce((state, item) => {
      if (!isEligible(item)) return state;
      const quantity = Number(item.quantity || 0);
      state.total += quantity;
      if (isSubscription(item)) state.subscription += quantity;
      else state.fullPrice += quantity;
      return state;
    }, { total: 0, fullPrice: 0, subscription: 0 });
  }

  function rulePairCount(cart) {
    const quantities = eligibleQuantities(cart);
    return Math.min(quantities.fullPrice, Math.floor(quantities.total / 2));
  }

  function appliedPairCount(cart) {
    let discountedUnits = 0;
    for (const item of (cart.items || cart.lines || [])) {
      if (!isEligible(item)) continue;
      for (const allocation of bogoAllocations(item)) {
        const amount = allocationAmountCents(allocation);
        // Shopify may copy the automatic-discount allocation onto the full-price
        // Buy line with a zero amount when it splits one cart key into Buy/Get
        // rows. Only positive allocations represent discounted Get units.
        if (amount <= 0) continue;
        const sellingPlanPrice = Number(
          item.selling_plan_allocation?.per_delivery_price ||
          item.sellingPlanAllocation?.priceAdjustments?.[0]?.price?.amount * 100 ||
          0
        );
        const unitBasis = sellingPlanPrice || Number(item.original_price || item.price || item.final_price || 0);
        const halfUnit = Math.max(1, unitBasis * 0.5);
        discountedUnits += Math.max(1, Math.round(amount / halfUnit));
      }
    }
    return discountedUnits;
  }

  function nextPairRequirement(cart, pairCount) {
    const quantities = eligibleQuantities(cart);
    const targetPairs = pairCount + 1;
    const requiredFullPrice = Math.max(0, targetPairs - quantities.fullPrice);
    const totalAfterRequiredBuyers = quantities.total + requiredFullPrice;
    const additionalEligible = Math.max(0, (targetPairs * 2) - totalAfterRequiredBuyers);
    return {
      items: requiredFullPrice + additionalEligible,
      fullPriceItems: requiredFullPrice,
      additionalEligibleItems: additionalEligible
    };
  }

  function analysisForCart(cart) {
    const quantities = eligibleQuantities(cart);
    const rulePairs = rulePairCount(cart);
    const appliedPairs = appliedPairCount(cart);
    // A visible Shopify BOGO allocation is authoritative. The rule count covers the
    // brief interval before Shopify's refreshed cart payload exposes the allocation.
    const pairCount = Math.max(appliedPairs, rulePairs);
    return {
      ...quantities,
      rulePairs,
      appliedPairs,
      pairCount,
      next: nextPairRequirement(cart, pairCount)
    };
  }

  function messageForCart(cart) {
    const state = analysisForCart(cart);
    const plural = state.pairCount === 1 ? 'PAIR' : 'PAIRS';
    const another = state.pairCount > 0 ? 'another 50% off' : '50% off';
    let title;
    let hint = '';

    if (state.next.items === 1 && state.next.fullPriceItems === 1) {
      title = `Add 1 full-price eligible item to unlock ${another}`;
    } else if (state.next.items === 1) {
      title = `Add 1 more eligible item to unlock ${another}`;
    } else if (state.pairCount > 0) {
      title = '50% off unlocked';
      hint = state.next.fullPriceItems > 0
        ? `Add ${state.next.items} more eligible items, including ${state.next.fullPriceItems} full-price purchase${state.next.fullPriceItems === 1 ? '' : 's'}, to unlock another 50% off.`
        : `Add ${state.next.items} more eligible items to unlock another 50% off.`;
    } else {
      title = state.next.fullPriceItems > 0
        ? `Add ${state.next.items} eligible items, including ${state.next.fullPriceItems} full-price purchase${state.next.fullPriceItems === 1 ? '' : 's'}, to unlock 50% off`
        : `Add ${state.next.items} eligible items to unlock 50% off`;
    }

    return {
      ...state,
      eyebrow: `${state.pairCount} BOGO ${plural} UNLOCKED`,
      title,
      hint,
      detail: DISCLAIMER,
      fill: state.next.items <= 1 ? 50 : (state.pairCount > 0 ? 100 : 0),
      state: state.pairCount > 0 ? (state.next.items === 1 ? 'one-away' : 'unlocked') : 'start'
    };
  }

  function ensureRoot() {
    const rewardProgress = document.querySelector('.rebuy-cart__progress-bar-wrapper');
    if (!rewardProgress) return null;
    let root = document.getElementById(ROOT_ID);
    if (!root) {
      root = document.createElement('section');
      root.id = ROOT_ID;
      root.className = 'prspr-bogo-progress';
      root.setAttribute('aria-live', 'polite');
      root.innerHTML = `
        <div class="prspr-bogo-progress__eyebrow"></div>
        <div class="prspr-bogo-progress__track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
          <span class="prspr-bogo-progress__fill"></span>
        </div>
        <strong class="prspr-bogo-progress__title"></strong>
        <span class="prspr-bogo-progress__hint"></span>
        <span class="prspr-bogo-progress__detail"></span>`;
      rewardProgress.parentNode.insertBefore(root, rewardProgress);
    }
    return root;
  }

  function render(cart) {
    const root = ensureRoot();
    if (!root) return false;
    const copy = messageForCart(cart);
    root.dataset.state = copy.state;
    root.querySelector('.prspr-bogo-progress__eyebrow').textContent = copy.eyebrow;
    root.querySelector('.prspr-bogo-progress__title').textContent = copy.title;
    const hint = root.querySelector('.prspr-bogo-progress__hint');
    hint.textContent = copy.hint;
    hint.hidden = !copy.hint;
    root.querySelector('.prspr-bogo-progress__detail').textContent = copy.detail;
    root.querySelector('.prspr-bogo-progress__fill').style.width = `${copy.fill}%`;
    const track = root.querySelector('[role="progressbar"]');
    track.setAttribute('aria-valuenow', String(copy.fill));
    track.setAttribute('aria-label', `${copy.eyebrow}. ${copy.title}. ${copy.hint} ${copy.detail}`.trim());
    return true;
  }

  function cartSignature(cart) {
    return (cart.items || []).map((item) => {
      const discounts = bogoAllocations(item).map((a) => `${allocationTitle(a)}:${allocationAmountCents(a)}`).join(',');
      return `${item.variant_id}:${item.quantity}:${isSubscription(item) ? 'sub' : 'full'}:${discounts}`;
    }).join('|');
  }

  async function refresh() {
    if (requestInFlight || !document.querySelector('.rebuy-cart')) return;
    requestInFlight = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      const signature = cartSignature(cart);
      if (signature !== lastSignature || !document.getElementById(ROOT_ID)) {
        lastSignature = signature;
        render(cart);
      }
    } catch (_) {
      // Rebuy and the native cart continue normally if this enhancement cannot refresh.
    } finally {
      requestInFlight = false;
    }
  }

  function schedule() {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(refresh, 80);
  }

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('rebuy:cart.change', schedule);
  document.addEventListener('cart:updated', schedule);
  window.addEventListener('pageshow', schedule);
  window.setInterval(() => {
    if (document.querySelector('.rebuy-cart__flyout')) refresh();
  }, 2000);

  window.__PRSPR_BOGO_PROGRESS__ = {
    isEligible,
    isSubscription,
    eligibleQuantities,
    rulePairCount,
    appliedPairCount,
    analysisForCart,
    messageForCart,
    render,
    refresh
  };
  schedule();
})();
